import { isMarkerId } from "../../../util/shapeTypeMapper";

/**
 * Same logical text: API Text shape (shape_id, inner id) or shared text_box_id.
 */
export const canvasTextsRepresentSameEntity = (a, b) => {
  if (!a || !b) return false;
  const sa = a.shape_id && !isMarkerId(a.shape_id) ? String(a.shape_id) : null;
  const sb = b.shape_id && !isMarkerId(b.shape_id) ? String(b.shape_id) : null;
  const ia = a.id != null && !isMarkerId(a.id) ? String(a.id) : null;
  const ib = b.id != null && !isMarkerId(b.id) ? String(b.id) : null;
  if (sa && sb && sa === sb) return true;
  if (ia && ib && ia === ib) return true;
  if (sa && ib && sa === ib) return true;
  if (sb && ia && sb === ia) return true;
  // User-created / cloned texts (marker IDs or createdOnCanvas) can legitimately
  // share text_box_id with their source. Never collapse them by text_box_id alone.
  const aIsUserText =
    a.createdOnCanvas === true || isMarkerId(a.id) || isMarkerId(a.shape_id);
  const bIsUserText =
    b.createdOnCanvas === true || isMarkerId(b.id) || isMarkerId(b.shape_id);
  if (aIsUserText || bIsUserText) return false;
  const tba = a.text_box_id ? String(a.text_box_id) : null;
  const tbb = b.text_box_id ? String(b.text_box_id) : null;
  if (tba && tbb && tba === tbb) return true;
  return false;
};

const mergeGeomFrom = (target, source) => ({
  ...target,
  x: source.x,
  y: source.y,
  width: source.width,
  height: source.height,
  rotation: source.rotation ?? target.rotation,
  scaleX: source.scaleX ?? target.scaleX,
  scaleY: source.scaleY ?? target.scaleY,
  canvas_page_location_and_size:
    source.canvas_page_location_and_size ?? target.canvas_page_location_and_size,
  _stageX: source._stageX,
  _stageY: source._stageY,
});

export const dedupeCanvasTextsByEntity = (texts) => {
  if (!Array.isArray(texts) || texts.length < 2) return texts || [];
  const list = [...texts];
  let changed = true;
  while (changed) {
    changed = false;
    outer: for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (canvasTextsRepresentSameEntity(list[i], list[j])) {
          const a = list[i];
          const b = list[j];
          const aTime = Number(a._lastUpdated) || 0;
          const bTime = Number(b._lastUpdated) || 0;
          const primary = aTime >= bTime ? a : b;
          const secondary = aTime >= bTime ? b : a;
          const shapeId =
            primary.shape_id ||
            secondary.shape_id ||
            (primary.id && !isMarkerId(primary.id) ? primary.id : null) ||
            (secondary.id && !isMarkerId(secondary.id) ? secondary.id : null);
          const canonicalId =
            shapeId && !isMarkerId(shapeId)
              ? shapeId
              : primary.id || secondary.id;
          list[i] = {
            ...secondary,
            ...primary,
            id: canonicalId,
            shape_id: shapeId || primary.shape_id || secondary.shape_id,
            text_box_id: primary.text_box_id || secondary.text_box_id,
          };
          list.splice(j, 1);
          changed = true;
          break outer;
        }
      }
    }
  }
  return list;
};

/**
 * Prefer IndexedDB geometry when timestamps tie or IDB is newer (form navigation state is often stale).
 */
export const mergeIndexedDbTextsWithNavigationState = (
  idbTexts,
  incomingTexts,
  options = {},
) => {
  const { appendUnmatchedIncoming = true } = options || {};
  if (!Array.isArray(idbTexts) || idbTexts.length === 0) return incomingTexts || [];
  if (!Array.isArray(incomingTexts) || incomingTexts.length === 0) return idbTexts;
  const usedIncoming = new Set();
  const result = idbTexts.map((idbT) => {
    const idx = incomingTexts.findIndex((inc) =>
      canvasTextsRepresentSameEntity(idbT, inc),
    );
    if (idx < 0) return idbT;
    usedIncoming.add(idx);
    const inc = incomingTexts[idx];
    const idbTime = Number(idbT._lastUpdated) || 0;
    const incTime = Number(inc._lastUpdated) || 0;
    const preferIdbGeom = idbTime >= incTime;
    const merged = { ...idbT, ...inc };
    if (preferIdbGeom) {
      return mergeGeomFrom(merged, idbT);
    }
    return merged;
  });
  if (appendUnmatchedIncoming) {
    incomingTexts.forEach((inc, idx) => {
      if (usedIncoming.has(idx)) return;
      if (result.some((r) => canvasTextsRepresentSameEntity(r, inc))) return;
      result.push(inc);
    });
  }
  return dedupeCanvasTextsByEntity(result);
};

const TB_PREFIX = "tb:";

export const registerCanvasTextKeysInMap = (map, t) => {
  if (!t || !(map instanceof Map)) return;
  const sid = t.shape_id && !isMarkerId(t.shape_id) ? String(t.shape_id) : null;
  const innerId = t.id != null && !isMarkerId(t.id) ? String(t.id) : null;
  const tb = t.text_box_id ? String(t.text_box_id) : null;
  if (sid) map.set(sid, t);
  if (innerId && innerId !== sid) map.set(innerId, t);
  if (tb) map.set(`${TB_PREFIX}${tb}`, t);
};

export const textShapeAlreadyLoaded = (map, textIdFromShape, shapeProperties) => {
  if (!map || !(map instanceof Map)) return false;
  if (textIdFromShape && map.has(String(textIdFromShape))) return true;
  const innerId = shapeProperties?.id;
  if (innerId != null && !isMarkerId(innerId) && map.has(String(innerId)))
    return true;
  const tb = shapeProperties?.text_box_id;
  if (tb && map.has(`${TB_PREFIX}${String(tb)}`)) return true;
  return false;
};
